import type { Confirmation, FormDoc, FormFont, FormSettings, FormTheme } from "@koaform/form-schema"; // prettier-ignore
import { FONT_LABELS, FONT_STACKS, resolveThemeUrl } from "@koaform/ui";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import { useEffect, useRef, useState } from "react";

import { api } from "../lib/api.js";
import { ImageCropDialog } from "./ImageCropDialog.js";

const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:5050";

// See FieldSettings: the margin belongs on the label TEXT, not on the
// <label> that wraps the control.
const labelCls = "mb-2 block text-[12.5px] font-semibold text-body";
/** Spacing between one labelled control and the next. */
const fieldCls = "mb-5 block";
const inputCls =
  "block w-full appearance-none rounded-[10px] border border-line-input px-3.5 py-[11px] text-sm text-ink placeholder-faint focus:border-primary-600 focus:outline-none";
const sectionCls =
  "mt-6 mb-3 border-t border-line pt-5 font-mono text-[10px] tracking-[0.1em] text-faint";

const ACCENTS = ["#1d6be5", "#7c3aed", "#0f8a5f", "#d97706", "#dc2626"]; // prettier-ignore
const PAGES = ["#f6f9fd", "#ffffff", "#fdf6ec", "#eef7f1", "#10203a"]; // prettier-ignore

/** A row of color swatches with a "default" slot and a free-pick well. */
const ColorRow = ({
  label,
  options,
  value,
  onPick,
}: {
  label: string;
  options: string[];
  value: string | undefined;
  onPick: (color: string | undefined) => void;
}) => (
  <div className="mb-4">
    <span className={labelCls}>{label}</span>
    <div className="flex items-center gap-1.5">
      <button
        type="button"
        title="Default"
        aria-label={`${label}: default`}
        onClick={() => onPick(undefined)}
        className={`flex h-7 w-7 items-center justify-center rounded-full border text-[11px] text-faint ${
          value === undefined
            ? "border-primary-600 ring-2 ring-primary-100"
            : "border-line-bold hover:border-primary-600"
        }`}
      >
        ✕
      </button>
      {options.map((color) => (
        <button
          key={color}
          type="button"
          aria-label={`${label}: ${color}`}
          onClick={() => onPick(color)}
          style={{ backgroundColor: color }}
          className={`h-7 w-7 rounded-full border ${
            value === color
              ? "border-primary-600 ring-2 ring-primary-100"
              : "border-line-bold"
          }`}
        />
      ))}
      <label
        className="relative h-7 w-7 cursor-pointer overflow-hidden rounded-full border border-dashed border-line-bold"
        title="Custom color"
        style={
          value && !options.includes(value)
            ? { backgroundColor: value, borderStyle: "solid" }
            : { background: "conic-gradient(#f66,#fc6,#6c6,#69f,#c6f,#f66)" }
        }
      >
        <input
          type="color"
          aria-label={`${label}: custom`}
          value={value ?? "#1d6be5"}
          onChange={(e) => onPick(e.target.value)}
          className="absolute inset-0 cursor-pointer opacity-0"
        />
      </label>
    </div>
  </div>
);

/** Upload / replace / remove one theme image, with a small live thumbnail. */
const ImageRow = ({
  label,
  hint,
  url,
  round = false,
  onPick,
  onRemove,
}: {
  label: string;
  hint: string;
  url: string | undefined;
  round?: boolean;
  onPick: (file: File) => void;
  onRemove: () => void;
}) => {
  const input = useRef<HTMLInputElement>(null);
  const resolved = resolveThemeUrl(url, API_URL);

  return (
    <div className="mb-4">
      <span className={labelCls}>{label}</span>
      <div className="flex items-center gap-2.5">
        {resolved ? (
          <img
            src={resolved}
            alt=""
            className={`h-11 flex-none border border-line-strong object-cover ${
              round ? "w-11 rounded-full" : "w-[88px] rounded-[9px]"
            }`}
          />
        ) : (
          <span
            className={`grid h-11 flex-none place-items-center border-[1.5px] border-dashed border-line-bold text-[15px] text-faint ${
              round ? "w-11 rounded-full" : "w-[88px] rounded-[9px]"
            }`}
          >
            +
          </span>
        )}
        <button
          type="button"
          onClick={() => input.current?.click()}
          className="rounded-[9px] border border-line-bold bg-surface px-3 py-2 text-[13px] font-semibold text-ink hover:border-primary-600"
        >
          {resolved ? "Replace" : "Upload"}
        </button>
        {resolved ? (
          <button
            type="button"
            onClick={onRemove}
            className="text-[13px] font-semibold text-faint hover:text-danger"
          >
            Remove
          </button>
        ) : null}
        <input
          ref={input}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            // Same file twice must still fire — clear the input's memory.
            e.target.value = "";
            if (file) onPick(file);
          }}
        />
      </div>
      <p className="mt-1.5 text-[12.5px] leading-[1.5] text-faint">{hint}</p>
    </div>
  );
};

/**
 * The typeface picker.
 *
 * A hand-built listbox rather than a native <select>, because the whole point
 * is that each font's NAME is set in that font — and macOS ignores
 * font-family on <option> entirely, which is what forced a separate preview
 * line before. Here the label is the sample, so no preview is needed.
 *
 * A dropdown rather than a grid of tiles because the list is meant to keep
 * growing, and tiles could not without eating the panel.
 */
const FontRow = ({
  value,
  onPick,
}: {
  value: FormFont | undefined;
  onPick: (font: FormFont | undefined) => void;
}) => {
  const [open, setOpen] = useState(false);
  const current = value ?? "default";
  const fonts = Object.keys(FONT_STACKS) as FormFont[];

  // Any click elsewhere closes the list, the way a native select behaves.
  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [open]);

  return (
    <div className="mb-4">
      <span className={labelCls}>Font</span>
      <div className="relative" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          aria-haspopup="listbox"
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
          className={`${inputCls} mb-0 flex items-center justify-between gap-2 text-left ${
            open ? "border-primary-600" : ""
          }`}
        >
          <span
            className="truncate text-[15px] text-ink"
            style={{ fontFamily: FONT_STACKS[current] }}
          >
            {FONT_LABELS[current]}
          </span>
          <ChevronDownIcon className="size-4 flex-none text-faint" />
        </button>

        {open ? (
          <ul
            role="listbox"
            className="animate-rise absolute top-full right-0 left-0 z-20 mt-1.5 max-h-[280px] list-none overflow-auto rounded-[10px] border border-line-strong bg-surface p-1 shadow-[0_18px_44px_-16px_rgba(12,26,46,0.35)]"
          >
            {fonts.map((font) => {
              const active = current === font;
              return (
                <li key={font}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={active}
                    onClick={() => {
                      onPick(font === "default" ? undefined : font);
                      setOpen(false);
                    }}
                    // Each name is set in its own face — the sample IS the
                    // label, so no separate preview line is needed. A native
                    // <option> cannot do this: macOS ignores font-family on
                    // options entirely, which is why the preview existed.
                    style={{ fontFamily: FONT_STACKS[font] }}
                    className={`block w-full truncate rounded-[7px] px-3 py-2.5 text-left text-[15px] ${
                      active
                        ? "bg-primary-50 text-primary-600"
                        : "text-ink hover:bg-paper"
                    }`}
                  >
                    {FONT_LABELS[font]}
                  </button>
                </li>
              );
            })}
          </ul>
        ) : null}
      </div>
    </div>
  );
};

/** The design panel's on/off row. */
const Toggle = ({
  title,
  hint,
  on,
  onFlip,
}: {
  title: string;
  hint: string;
  on: boolean;
  onFlip: () => void;
}) => (
  <button
    type="button"
    role="switch"
    aria-checked={on}
    onClick={onFlip}
    className="mb-2.5 flex w-full cursor-pointer items-center justify-between gap-3 rounded-[10px] border border-line-input px-[13px] py-3 text-left hover:border-primary-600"
  >
    <span>
      <span className="block text-sm font-semibold text-ink">{title}</span>
      <span className="mt-0.5 block text-xs leading-[1.5] text-faint">
        {hint}
      </span>
    </span>
    <span
      className={`flex h-[23px] w-10 flex-none rounded-full p-[3px] transition-colors ${
        on ? "justify-end bg-primary-600" : "justify-start bg-line-bold"
      }`}
    >
      <span className="h-[17px] w-[17px] rounded-full bg-white" />
    </span>
  </button>
);

/** A URL input that only commits once the value parses as http(s). */
const UrlField = ({
  label,
  value,
  placeholder,
  hint,
  onCommit,
}: {
  label: string;
  value: string;
  placeholder: string;
  hint: string;
  onCommit: (url: string | undefined) => void;
}) => {
  const [draft, setDraft] = useState(value);
  const [bad, setBad] = useState(false);

  const commit = () => {
    const url = draft.trim();
    if (url === "") {
      setBad(false);
      onCommit(undefined);
      return;
    }
    const ok = /^https?:\/\//i.test(url) && URL.canParse(url);
    setBad(!ok);
    if (ok) onCommit(url);
  };

  return (
    <label className={fieldCls}>
      <span className={labelCls}>{label}</span>
      <input
        type="url"
        placeholder={placeholder}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => e.key === "Enter" && commit()}
        className={inputCls}
      />
      <span
        className={`mt-1.5 block text-[12.5px] leading-[1.5] ${bad ? "text-danger" : "text-faint"}`}
      >
        {bad ? "Needs to be a full http(s) link." : hint}
      </span>
    </label>
  );
};

/**
 * Everything about the form itself — shown when no block is selected.
 *
 * Look, what happens after submit, and who can reach it. All of this already
 * existed in the document schema; until this panel none of it had a UI, which
 * made "Thanks for your submission!" effectively hardcoded.
 */
export const FormDesign = ({
  doc,
  onChange,
  ensureFormId,
  published,
}: {
  doc: FormDoc;
  onChange: (doc: FormDoc) => void;
  /** Flushes the draft and resolves with its id — uploads need one to exist. */
  ensureFormId: () => Promise<string | null>;
  /** Closing a form and response notifications only mean anything once live. */
  published: boolean;
}) => {
  const settings = doc.settings;
  const theme = settings.theme;
  const [crop, setCrop] = useState<{ slot: "logo" | "banner"; file: File } | null>(null); // prettier-ignore
  const [uploadError, setUploadError] = useState<string | null>(null);

  const patch = (changes: Partial<FormSettings>) =>
    onChange({ ...doc, settings: { ...settings, ...changes } });

  const setTheme = (change: Partial<FormTheme>) => {
    const next: FormTheme = { ...theme, ...change };
    (Object.keys(next) as (keyof FormTheme)[]).forEach((k) => {
      if (next[k] === undefined) delete next[k];
    });
    patch({ theme: next });
  };

  const confirmation = settings.confirmation;
  const setConfirmation = (next: Confirmation) => patch({ confirmation: next });
  // True while the user is typing a redirect URL that has not committed yet —
  // the stored confirmation stays a message until the URL parses.
  const [redirectDraft, setRedirectDraft] = useState(false);
  const confirmationKind = redirectDraft ? "redirect" : confirmation.type;

  const segBtn = (active: boolean) =>
    `flex-1 rounded-[8px] px-2 py-2 text-[12.5px] font-semibold ${
      active
        ? "bg-surface text-ink shadow-sm"
        : "bg-transparent text-muted hover:text-ink"
    }`;

  const uploadCropped = async (slot: "logo" | "banner", blob: Blob) => {
    setCrop(null);
    setUploadError(null);
    try {
      const formId = await ensureFormId();
      if (!formId) {
        setUploadError("Give the form a title or a question first.");
        return;
      }
      const { url } = await api.uploadThemeImage(formId, slot, blob);
      setTheme(slot === "logo" ? { logoUrl: url } : { bannerUrl: url });
    } catch (err) {
      setUploadError(
        err instanceof Error ? err.message : "Could not upload that image.",
      );
    }
  };

  return (
    <div className="px-[18px] py-5">
      <h2 className="font-mono text-[10px] tracking-[0.1em] text-faint">
        DESIGN
      </h2>
      <div className="mt-3.5">
        <ColorRow
          label="Accent color"
          options={ACCENTS}
          value={theme.accent}
          onPick={(accent) => setTheme({ accent })}
        />
        <ColorRow
          label="Page color"
          options={PAGES}
          value={theme.background}
          onPick={(background) => setTheme({ background })}
        />
        <FontRow value={theme.font} onPick={(font) => setTheme({ font })} />
        <ImageRow
          label="Logo"
          hint="Square, shown at the top of the form."
          url={theme.logoUrl}
          round
          onPick={(file) => setCrop({ slot: "logo", file })}
          onRemove={() => setTheme({ logoUrl: undefined })}
        />
        <ImageRow
          label="Banner"
          hint="Wide image above the form — you can crop after picking."
          url={theme.bannerUrl}
          onPick={(file) => setCrop({ slot: "banner", file })}
          onRemove={() => setTheme({ bannerUrl: undefined })}
        />
        {uploadError ? (
          <p role="alert" className="mb-3 text-[12.5px] text-danger">
            {uploadError}
          </p>
        ) : null}
      </div>

      <h2 className={sectionCls}>SUBMIT BUTTON</h2>
      <label className={fieldCls}>
        <span className={labelCls}>Button label</span>
        <input
          className={inputCls}
          maxLength={60}
          value={settings.submitText}
          onChange={(e) => patch({ submitText: e.target.value })}
          onBlur={() => {
            if (!settings.submitText.trim()) patch({ submitText: "Submit" });
          }}
        />
      </label>

      <h2 className={sectionCls}>AFTER SUBMIT</h2>
      <div className="mb-2.5 flex gap-1 rounded-[10px] bg-line p-1">
        <button
          type="button"
          onClick={() => {
            setRedirectDraft(false);
            if (confirmation.type !== "message") {
              setConfirmation({
                type: "message",
                message: "Thanks for your submission!",
              });
            }
          }}
          className={segBtn(confirmationKind === "message")}
        >
          Show a message
        </button>
        <button
          type="button"
          onClick={() => setRedirectDraft(true)}
          className={segBtn(confirmationKind === "redirect")}
        >
          Redirect to a URL
        </button>
      </div>

      {confirmationKind === "redirect" ? (
        <UrlField
          label="Redirect respondents to"
          value={confirmation.type === "redirect" ? confirmation.url : ""}
          placeholder="https://your-site.com/thanks"
          hint="Opens right after a successful submit."
          onCommit={(url) => {
            // A half-typed redirect never reaches the schema: until a URL
            // parses, the stored confirmation stays a message.
            setRedirectDraft(Boolean(url));
            setConfirmation(
              url
                ? { type: "redirect", url }
                : { type: "message", message: "Thanks for your submission!" },
            );
          }}
        />
      ) : (
        <label className={fieldCls}>
          <span className={labelCls}>Thank-you message</span>
          <textarea
            className={inputCls}
            rows={3}
            maxLength={2000}
            value={confirmation.type === "message" ? confirmation.message : ""}
            onChange={(e) =>
              setConfirmation({ type: "message", message: e.target.value })
            }
          />
        </label>
      )}

      {/* Live-form settings. Before publish they would only be noise —
          nothing can respond to, or close, a form that is not live. */}
      {published ? (
        <>
          <h2 className={sectionCls}>ACCESS</h2>
          <Toggle
            title="Closed for responses"
            hint="Visitors see a closed notice instead of the form."
            on={settings.closed}
            onFlip={() => patch({ closed: !settings.closed })}
          />
          {settings.closed ? (
            <label className={fieldCls}>
              <span className={labelCls}>Closed message</span>
              <input
                className={inputCls}
                maxLength={500}
                placeholder="This form is closed."
                value={settings.closedMessage ?? ""}
                onChange={(e) =>
                  patch({ closedMessage: e.target.value || undefined })
                }
              />
            </label>
          ) : null}

          <h2 className={sectionCls}>NOTIFICATIONS</h2>
          <Toggle
            title="Email me new responses"
            hint="Sent to your account address only."
            on={settings.notifyOwner}
            onFlip={() => patch({ notifyOwner: !settings.notifyOwner })}
          />
          {/* The toggle reveals its own sub-setting, per the pattern used for
              every other option in this panel. */}
          {settings.notifyOwner ? (
            <div className="mb-2.5 flex gap-2">
              {(
                [
                  { id: "each", label: "Every response", hint: "One email each time someone submits." }, // prettier-ignore
                  { id: "digest", label: "Hourly summary", hint: "One email an hour, only when there is something new." }, // prettier-ignore
                ] as const
              ).map((opt) => {
                const on = (settings.notifyMode ?? "each") === opt.id;
                return (
                  <button
                    key={opt.id}
                    type="button"
                    title={opt.hint}
                    aria-pressed={on}
                    onClick={() => patch({ notifyMode: opt.id })}
                    className={`flex-1 rounded-[10px] border px-3 py-2.5 text-left ${
                      on
                        ? "border-primary-600 bg-primary-50"
                        : "border-line-input hover:border-primary-600"
                    }`}
                  >
                    <span className="block text-[13px] font-semibold text-ink">
                      {opt.label}
                    </span>
                    <span className="mt-0.5 block text-[11.5px] leading-[1.45] text-faint">
                      {opt.hint}
                    </span>
                  </button>
                );
              })}
            </div>
          ) : null}
          <p className="m-0 mb-2 text-[11.5px] leading-[1.5] text-faint">
            Above 50 responses in a day we switch to the summary automatically,
            whichever you pick — a form that goes viral must not send you
            thousands of emails.
          </p>
        </>
      ) : null}

      {crop ? (
        <ImageCropDialog
          file={crop.file}
          aspect={crop.slot === "logo" ? 1 : 3}
          targetWidth={crop.slot === "logo" ? 320 : 1500}
          title={crop.slot === "logo" ? "Crop your logo" : "Crop your banner"}
          round={crop.slot === "logo"}
          onDone={(blob) => void uploadCropped(crop.slot, blob)}
          onCancel={() => setCrop(null)}
        />
      ) : null}
    </div>
  );
};
